/*
TECH_DEBT: GoHighLevel Integration - Not ready for self-service

This integration is currently restricted to localhost-only access for internal testing purposes.
Before enabling self-service customer access, the following must be completed:

1. Real-time sync status tracking:
   - Persist job state on server (status, startedAt, finishedAt, progress, errors)
   - Expose job status via API endpoint or include in settings response
   - Implement SignalR or polling to show live progress to users

2. Robust error handling and recovery:
   - Handle partial sync failures gracefully
   - Provide clear error messages to users
   - Implement retry mechanisms for failed operations
   - Add detailed logging for troubleshooting

3. User experience improvements:
   - Show progress indicators (e.g., "Syncing 45/100 clients...")
   - Display sync history and audit log
   - Prevent overlapping operations via server-side state (not client-side flags)
   - Add validation for GoHighLevel API credentials before allowing sync

4. Remove localhost restriction:
   - Remove isLocalhost check (line 120) after completing above requirements
   - Update rate limiting if needed for production usage
   - Add comprehensive end-to-end testing

Current workaround: Buttons disabled on prod/dev with message to contact support.
*/

import * as React from 'react';
import { FC, useState, useEffect, useCallback } from 'react';
import { Form, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from 'reactstrap';
import { SettingsItemContentProps } from '../SettingsItem';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { IntegrationsSettingsData, IntegrationGoHighLevelSettings, IntegrationType, ProfessionalData } from '../../../models';
import { TextInputField, CheckInputField } from '../../../components/Forms/fields';
import { Row } from '../../../components/Forms/table/Row';
import { Col } from '../../../components/Forms/table/Column';
import Typography from '../../../components/Typography';
import ToolTip from '../../../components/ToolTip';
import { useDispatch, useSelector } from 'react-redux';
import { getSettings } from '../../../store/settings/selectors';
import RefreshIcon from '@material-ui/icons/Refresh';
import { IconButton } from '@material-ui/core';
import moment from 'moment';
import { syncIntegrationAsync, syncGoHighLevelCalendarsAsync, clearIntegrationAsync, getGoHighLevelUsersAsync, GhlUserOption, getGoHighLevelAuditLogAsync, GhlAuditLogEntry } from '../../../ajax/requests';
import { alertError, alertSuccess } from '../../../store/alerts/actions';
import { loadSettings } from '../../../store/settings/actions';
import { ButtonsContainer } from '../SettingsItem/styled';
import StyledTable from '../../../components/StyledTable';
import { useEntities } from '../../../store/entities/hooks';
import { getErrorMessage } from '../../../ajax/errors';
import AppButton from '../../../components/AppButton';

export interface IntegrationsSettingsProps extends SettingsItemContentProps {
    values: IntegrationsSettingsData,
    onChange: (settings: IntegrationsSettingsData, save?: boolean) => void,
}

export const GoHighLevelSettings : FC<IntegrationsSettingsProps> = ({
    id,
    values,
    onChange,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const savedSettings = useSelector(getSettings);
    const professionals = useEntities<ProfessionalData>('professionals');
    const [showSyncConfirmation, setShowSyncConfirmation] = useState(false);
    const [showClearConfirmation, setShowClearConfirmation] = useState(false);
    const [ghlUsers, setGhlUsers] = useState<GhlUserOption[]>([]);
    const [ghlUsersLoading, setGhlUsersLoading] = useState(false);
    const [ghlUsersError, setGhlUsersError] = useState<string | null>(null);
    const [syncCalendarsLoading, setSyncCalendarsLoading] = useState(false);
    const [auditLog, setAuditLog] = useState<GhlAuditLogEntry[]>([]);
    const [auditLogLoading, setAuditLogLoading] = useState(false);
    const [auditLogError, setAuditLogError] = useState<string | null>(null);
    const [auditLogOffset, setAuditLogOffset] = useState(0);
    const [auditLogHasMore, setAuditLogHasMore] = useState(true);
    const [auditLogVisible, setAuditLogVisible] = useState(false);
    const PAGE_SIZE = 25;

    const handleSync = async () => {
        setShowSyncConfirmation(false);
        try {
            await syncIntegrationAsync(IntegrationType.GoHighLevel);
            dispatch(alertSuccess(t(TK.integrationSyncInitiated)));
        } catch (error) {
            const message = error instanceof Error ? error.message : `${error ?? ''}`;
            dispatch(alertError(t(TK.goHighLevelSyncUnexpectedError, message || t(TK.unknown))));
        }
    };

    const handleClear = async () => {
        setShowClearConfirmation(false);
        try {
            await clearIntegrationAsync(IntegrationType.GoHighLevel);
            dispatch(alertSuccess(t(TK.integrationClearInitiated)));
        } catch (error) {
            const message = error instanceof Error ? error.message : `${error ?? ''}`;
            dispatch(alertError(t(TK.goHighLevelClearUnexpectedError, message || t(TK.unknown))));
        }
    };

    const handleSyncCalendars = async () => {
        setSyncCalendarsLoading(true);
        try {
            const result = await syncGoHighLevelCalendarsAsync();
            dispatch(alertSuccess(result.message ?? t(TK.goHighLevelSyncCalendars)));
        } catch (error) {
            const message = getErrorMessage(error) ?? (error instanceof Error ? error.message : `${error ?? ''}`);
            dispatch(alertError(t(TK.goHighLevelSyncUnexpectedError, message || t(TK.unknown))));
        } finally {
            setSyncCalendarsLoading(false);
        }
    };

    const handleRefresh = async () => {
        try {
            await dispatch(loadSettings());
            dispatch(alertSuccess(t(TK.settingsReloaded)));
        } catch (error) {
            dispatch(alertError(t(TK.somethingWentWrong)));
        }
    };

    const fetchGhlUsers = useCallback(async () => {
        setGhlUsersLoading(true);
        setGhlUsersError(null);
        try {
            const users = await getGoHighLevelUsersAsync();
            setGhlUsers(users);
        } catch {
            setGhlUsersError(t(TK.goHighLevelLoadGhlUsersFailed));
            setGhlUsers([]);
        } finally {
            setGhlUsersLoading(false);
        }
    }, [t]);

    const loadAuditLog = useCallback(async (reset = false) => {
        const offset = reset ? 0 : auditLogOffset;
        setAuditLogLoading(true);
        setAuditLogError(null);
        try {
            const entries = await getGoHighLevelAuditLogAsync(PAGE_SIZE, offset);
            setAuditLog(prev => reset ? entries : [...prev, ...entries]);
            setAuditLogOffset(offset + entries.length);
            setAuditLogHasMore(entries.length === PAGE_SIZE);
        } catch {
            setAuditLogError(t(TK.goHighLevelActivityLogLoadError));
        } finally {
            setAuditLogLoading(false);
        }
    }, [auditLogOffset, t]);

    const handleShowAuditLog = () => {
        if (!auditLogVisible) {
            setAuditLogVisible(true);
            loadAuditLog(true);
        } else {
            setAuditLogVisible(false);
        }
    };

    const goHighLevelSettings: IntegrationGoHighLevelSettings = values.goHighLevel ?? {
        privateIntegrationKey: '',
        locationId: '',
    }

    // Get saved settings
    const savedGoHighLevelSettings = savedSettings.integrations?.goHighLevel ?? {
        privateIntegrationKey: '',
        locationId: '',
    };

    const createCalendarPerServiceType = !!goHighLevelSettings.createCalendarPerServiceType;
    const associateProfessionals = !!goHighLevelSettings.associateProfessionalsWithGhlUsers;
    const professionalUserMappings = goHighLevelSettings.professionalUserMappings ?? {};
    const savedProfessionalUserMappings = savedGoHighLevelSettings.professionalUserMappings ?? {};

    // Auto-load GHL users whenever the association section becomes visible with valid saved credentials
    useEffect(() => {
        if (
            associateProfessionals &&
            savedGoHighLevelSettings.privateIntegrationKey &&
            savedGoHighLevelSettings.locationId &&
            ghlUsers.length === 0 &&
            !ghlUsersLoading &&
            !ghlUsersError
        ) {
            fetchGhlUsers();
        }
    }, [associateProfessionals, savedGoHighLevelSettings.privateIntegrationKey, savedGoHighLevelSettings.locationId, ghlUsers.length, ghlUsersLoading, ghlUsersError, fetchGhlUsers]);

    const handleProfessionalMappingChange = (professionalId: string, ghlUserId: string) => {
        const updated = { ...professionalUserMappings };
        if (ghlUserId) {
            updated[professionalId] = ghlUserId;
        } else {
            delete updated[professionalId];
        }
        onChange({ ...values, goHighLevel: { ...goHighLevelSettings, professionalUserMappings: updated } });
    };

    // Check if there are unsaved changes
    const hasUnsavedChanges =
        goHighLevelSettings.privateIntegrationKey !== savedGoHighLevelSettings.privateIntegrationKey ||
        goHighLevelSettings.locationId !== savedGoHighLevelSettings.locationId ||
        createCalendarPerServiceType !== !!savedGoHighLevelSettings.createCalendarPerServiceType ||
        associateProfessionals !== !!savedGoHighLevelSettings.associateProfessionalsWithGhlUsers ||
        JSON.stringify(professionalUserMappings) !== JSON.stringify(savedProfessionalUserMappings);

    // Check if running on localhost (feature restricted to internal use only)
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Enable buttons only on localhost with valid saved credentials and no unsaved changes
    const canSync = !!(
        isLocalhost &&
        savedGoHighLevelSettings.privateIntegrationKey &&
        savedGoHighLevelSettings.locationId &&
        !hasUnsavedChanges
    );
    const canSyncServiceCalendars = !!(
        isLocalhost &&
        createCalendarPerServiceType &&
        savedGoHighLevelSettings.privateIntegrationKey &&
        savedGoHighLevelSettings.locationId
    );
    
    return (
        <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
            <Alert color="secondary" isOpen>
                {t(TK.goHighLevelIntegrationDescription)}
            </Alert>
            
            <TextInputField
                id={`${id}-gohighlevel-private-key`}
                label={t(TK.goHighLevelPrivateIntegrationKeyLabel)}
                helpText={t(TK.goHighLevelPrivateKeyHelp)}
                value={goHighLevelSettings.privateIntegrationKey}
                type="password"
                onChange={privateIntegrationKey => onChange({...values, goHighLevel: {...goHighLevelSettings, privateIntegrationKey}})} />
            <TextInputField
                id={`${id}-gohighlevel-location-id`}
                label={t(TK.goHighLevelLocationIdLabel)}
                helpText={t(TK.goHighLevelLocationIdHelp)}
                value={goHighLevelSettings.locationId}
                type="text"
                onChange={locationId => onChange({...values, goHighLevel: {...goHighLevelSettings, locationId}})} />

            {/* Professional → GHL User mapping */}
            <>
                <hr style={{marginTop: 'var(--spacer-S)', marginBottom: 'var(--spacer-XS)'}} />
                <Typography size='medium'><b>{t(TK.configuration)}</b></Typography>
                <div style={{display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)'}}>
                    <CheckInputField
                        id={`${id}-calendar-per-service-type`}
                        label={t(TK.goHighLevelCreateCalendarPerServiceType)}
                        helpText={createCalendarPerServiceType ? t(TK.goHighLevelCreateCalendarPerServiceTypeTooltip) : undefined}
                        value={createCalendarPerServiceType}
                        onChange={checked => onChange({
                            ...values,
                            goHighLevel: { ...goHighLevelSettings, createCalendarPerServiceType: checked },
                        })}
                    />
                    {createCalendarPerServiceType && (
                        <>
                            <span id={`${id}-sync-calendars-button-wrapper`}>
                                <AppButton
                                    id={`${id}-sync-calendars-button`}
                                    color="secondary"
                                    size="sm"
                                    disabled={!canSyncServiceCalendars || syncCalendarsLoading}
                                    onClick={handleSyncCalendars}
                                >
                                    {syncCalendarsLoading ? t(TK.syncing) : t(TK.goHighLevelSyncCalendars)}
                                </AppButton>
                            </span>
                            <ToolTip
                                target={`${id}-sync-calendars-button-wrapper`}
                                toolTipContent={!isLocalhost ? t(TK.goHighLevelContactSupportToUse) : ''}
                            />
                        </>
                    )}
                </div>
                <CheckInputField
                    id={`${id}-associate-professionals`}
                    label={t(TK.goHighLevelAssociateProfessionals)}
                    helpText={t(TK.goHighLevelAssociateProfessionalsHelp)}
                    value={associateProfessionals}
                    onChange={checked => onChange({
                        ...values,
                        goHighLevel: { ...goHighLevelSettings, associateProfessionalsWithGhlUsers: checked },
                    })}
                />

                {associateProfessionals && (
                    <div style={{marginTop: 'var(--spacer-S)'}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacer-S)'}}>
                            <Typography size='medium'><b>{t(TK.goHighLevelProfessionalUserMappingTitle)}</b></Typography>
                            <AppButton
                                id={`${id}-reload-ghl-users`}
                                color="secondary"
                                size="sm"
                                disabled={ghlUsersLoading || !savedGoHighLevelSettings.privateIntegrationKey || !savedGoHighLevelSettings.locationId}
                                onClick={() => fetchGhlUsers()}
                            >
                                <RefreshIcon style={{fontSize: 'inherit', marginRight: 'var(--spacer-XXS)'}} />
                                {t(TK.refresh)}
                            </AppButton>
                        </div>

                        {ghlUsersLoading && (
                            <Typography size='small' style={{color: 'var(--text-muted)'}}>{t(TK.goHighLevelLoadingUsers)}</Typography>
                        )}
                        {ghlUsersError && (
                            <Alert color="danger" isOpen>{ghlUsersError}</Alert>
                        )}

                        {!ghlUsersLoading && (
                            <StyledTable scrollable hover size="sm">
                                <thead>
                                    <tr>
                                        <th>{t(TK.goHighLevelProfessionalColumn)}</th>
                                        <th>{t(TK.goHighLevelGhlUserColumn)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {professionals.map(professional => (
                                        <tr key={professional.id}>
                                            <td>{professional.name}</td>
                                            <td>
                                                <select
                                                    style={{
                                                        width: '100%',
                                                        padding: 'var(--spacer-XXS) var(--spacer-XS)',
                                                        border: 'var(--border-thin) solid var(--appLigthGrey)',
                                                        borderRadius: 'var(--spacer-XXS)',
                                                        background: 'var(--bg-light)',
                                                        fontSize: 'inherit',
                                                    }}
                                                    value={professionalUserMappings[professional.id] ?? ''}
                                                    onChange={e => handleProfessionalMappingChange(professional.id, e.target.value)}
                                                >
                                                    <option value="">{t(TK.goHighLevelNotMapped)}</option>
                                                    {ghlUsers.map(user => (
                                                        <option key={user.id} value={user.id}>
                                                            {user.name}{user.email ? ` (${user.email})` : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </StyledTable>
                        )}
                    </div>
                )}
            </>

            {/* Sync Statistics - Read-only */}
            <>
                <hr style={{marginTop: 'var(--spacer-M)', marginBottom: 'var(--spacer-S)'}} />
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacer-S)'}}>
                    <Typography size='medium'><b>{t(TK.syncStatisticsTitle)}</b></Typography>
                    <IconButton
                        id={`${id}-refresh-button`}
                        size="small"
                        aria-label={t(TK.refresh)}
                        onClick={handleRefresh}
                    >
                        <RefreshIcon style={{fontSize: '1.25rem'}} />
                    </IconButton>
                </div>
                <Row>
                    <Col md={6}>
                        <TextInputField
                            id={`${id}-gohighlevel-last-full-sync`}
                            label={t(TK.lastFullSync)}
                            value={savedGoHighLevelSettings.lastFullSync ? moment(savedGoHighLevelSettings.lastFullSync).format('LLL') : 'N/A'}
                            type="text"
                            disabled
                            onChange={() => {}} />
                    </Col>
                    <Col md={6}>
                        <TextInputField
                            id={`${id}-gohighlevel-last-sync`}
                            label={t(TK.lastSync)}
                            value={savedGoHighLevelSettings.lastSync ? moment(savedGoHighLevelSettings.lastSync).format('LLL') : 'N/A'}
                            type="text"
                            disabled
                            onChange={() => {}} />
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <TextInputField
                            id={`${id}-gohighlevel-total-clients-sync`}
                            label={t(TK.totalClientsSynced)}
                            value={savedGoHighLevelSettings.totalClientsSync?.toString() ?? 'N/A'}
                            type="text"
                            disabled
                            onChange={() => {}} />
                    </Col>
                    <Col md={6}>
                        <TextInputField
                            id={`${id}-gohighlevel-total-appointments-sync`}
                            label={t(TK.totalAppointmentsSynced)}
                            value={savedGoHighLevelSettings.totalAppointmentsSync?.toString() ?? 'N/A'}
                            type="text"
                            disabled
                            onChange={() => {}} />
                    </Col>
                </Row>
            </>

            {/* Activity Log */}
            <>
                <hr style={{marginTop: 'var(--spacer-M)', marginBottom: 'var(--spacer-S)'}} />
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: auditLogVisible ? 'var(--spacer-S)' : undefined}}>
                    <Typography size='medium'><b>{t(TK.goHighLevelActivityLog)}</b></Typography>
                    <AppButton
                        id={`${id}-audit-log-toggle`}
                        color="secondary"
                        size="sm"
                        onClick={handleShowAuditLog}
                    >
                        {auditLogVisible ? '▲' : '▼'} {t(TK.goHighLevelActivityLog)}
                    </AppButton>
                </div>

                {auditLogVisible && (
                    <div>
                        {auditLogError && (
                            <Alert color="danger" isOpen>{auditLogError}</Alert>
                        )}
                        {!auditLogLoading && auditLog.length === 0 && !auditLogError && (
                            <Typography size='small' style={{color: 'var(--text-muted)'}}>{t(TK.goHighLevelActivityLogEmpty)}</Typography>
                        )}
                        {auditLog.length > 0 && (
                            <StyledTable scrollable hover size="sm">
                                <thead>
                                    <tr>
                                        <th style={{whiteSpace: 'nowrap'}}>{t(TK.goHighLevelActivityLogTimestamp)}</th>
                                        <th>{t(TK.goHighLevelActivityLogLevel)}</th>
                                        <th>{t(TK.goHighLevelActivityLogEntityType)}</th>
                                        <th>{t(TK.goHighLevelActivityLogContext)}</th>
                                        <th>{t(TK.goHighLevelActivityLogMessage)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {auditLog.map(entry => (
                                        <tr key={entry.id}>
                                            <td style={{whiteSpace: 'nowrap', color: 'var(--text-muted)', fontSize: '0.85em'}}>
                                                {moment(entry.createdAt).format('LLL')}
                                            </td>
                                            <td>
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '0.1em var(--spacer-XS)',
                                                    borderRadius: 'var(--spacer-XXS)',
                                                    fontSize: '0.8em',
                                                    fontWeight: 600,
                                                    background: entry.level === 'error' ? 'var(--danger-color)' : entry.level === 'warning' ? 'var(--warning-color)' : 'var(--success-bg)',
                                                    color: entry.level === 'warning' ? '#333' : entry.level === 'info' ? '#333' : '#fff',
                                                }}>
                                                    {entry.level}
                                                </span>
                                            </td>
                                            <td style={{fontSize: '0.85em', color: 'var(--text-muted)', whiteSpace: 'nowrap'}}>{entry.entityType ?? '—'}</td>
                                            <td style={{fontSize: '0.85em', color: 'var(--text-muted)', whiteSpace: 'nowrap'}}>{entry.context}</td>
                                            <td style={{fontSize: '0.85em', wordBreak: 'break-word'}}>{entry.message}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </StyledTable>
                        )}
                        {auditLogLoading && (
                            <Typography size='small' style={{color: 'var(--text-muted)', marginTop: 'var(--spacer-XS)'}}>{t(TK.loading)}</Typography>
                        )}
                        {!auditLogLoading && auditLogHasMore && auditLog.length > 0 && (
                            <div style={{marginTop: 'var(--spacer-S)', textAlign: 'center'}}>
                                <AppButton
                                    id={`${id}-audit-log-load-more`}
                                    color="secondary"
                                    size="sm"
                                    onClick={() => loadAuditLog(false)}
                                >
                                    {t(TK.goHighLevelActivityLogLoadMore)}
                                </AppButton>
                            </div>
                        )}
                    </div>
                )}
            </>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 'var(--spacer-M)', gap: 'var(--spacer-M)' }}>
                <ButtonsContainer>
                    <span id={`${id}-delete-all-button-wrapper`}>
                        <AppButton
                            id={`${id}-clear-button`}
                            color="danger"
                            onClick={() => setShowClearConfirmation(true)}
                            disabled={!canSync}
                        >
                            {t(TK.deleteAll)}
                        </AppButton>
                    </span>
                    <ToolTip
                        target={`${id}-delete-all-button-wrapper`}
                        toolTipContent={!isLocalhost ? t(TK.goHighLevelContactSupportToUse) : t(TK.goHighLevelDeleteAllTooltip)}
                    />
                    <span id={`${id}-sync-button-wrapper`}>
                        <AppButton
                            id={`${id}-sync-button`}
                            color="success"
                            onClick={() => setShowSyncConfirmation(true)}
                            disabled={!canSync}
                        >
                            {t(TK.sync)}
                        </AppButton>
                    </span>
                    <ToolTip
                        target={`${id}-sync-button-wrapper`}
                        toolTipContent={!isLocalhost ? t(TK.goHighLevelContactSupportToUse) : ''}
                    />
                </ButtonsContainer>
            </div>

            {/* Sync Confirmation Modal */}
            <Modal isOpen={showSyncConfirmation} toggle={() => setShowSyncConfirmation(false)}>
                <ModalHeader toggle={() => setShowSyncConfirmation(false)}>
                    {t(TK.goHighLevelSyncConfirmationTitle)}
                </ModalHeader>
                <ModalBody>
                    {t(TK.goHighLevelSyncConfirmationMessage)}
                </ModalBody>
                <ModalFooter>
                    <AppButton
                        id={`${id}-sync-confirm-button`}
                        color="primary"
                        onClick={handleSync}
                    >
                        {t(TK.continueButton)}
                    </AppButton>
                    <AppButton
                        id={`${id}-sync-cancel-button`}
                        color="secondary"
                        onClick={() => setShowSyncConfirmation(false)}
                    >
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>

            {/* Clear Confirmation Modal */}
            <Modal isOpen={showClearConfirmation} toggle={() => setShowClearConfirmation(false)}>
                <ModalHeader toggle={() => setShowClearConfirmation(false)}>
                    {t(TK.goHighLevelClearConfirmationTitle)}
                </ModalHeader>
                <ModalBody>
                    {t(TK.goHighLevelClearConfirmationMessage)}
                </ModalBody>
                <ModalFooter>
                    <AppButton
                        id={`${id}-clear-confirm-button`}
                        color="danger"
                        onClick={handleClear}
                    >
                        {t(TK.continueButton)}
                    </AppButton>
                    <AppButton
                        id={`${id}-clear-cancel-button`}
                        color="secondary"
                        onClick={() => setShowClearConfirmation(false)}
                    >
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </Form>
    )
}
